import { ReactComponent as TrashIcon } from "../../assets/icons/tables/trash-icon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/tables/edit-icon.svg";
import { useState } from "react";
import { DeleteModal } from "../../components";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useAuth } from "../../store/useAuth";
import { useMarketPricing } from "../../store/useMarketPricing";
import { Tooltip } from "@material-tailwind/react";
import { useNavigate } from "react-router";

dayjs.extend(relativeTime);

export const MarketPricingTable = () => {
  /** Hooks */
  const { marketPrices, deleteMarketPricing, updateMarketPricing } =
    useMarketPricing();
  const { user } = useAuth();
  // Mirrors the backend: Admin, Store Admin and Super Admin may edit a
  // material, only a Super Admin may delete one.
  const canEditMaterials = [1, 5, 7].includes(Number(user?.user_role));
  const canDeleteMaterials = Number(user?.user_role) === 7;
  const navigate = useNavigate();
  /** State */
  const [isOpen, setIsOpen] = useState(false);

  const toggleModal = () => setIsOpen(!isOpen);

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
        <thead className="font-medium bg-gray-50">
          <tr>
            {/* First column: the list is served ordered by this value, so
                showing it makes the app's material order visible here. */}
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Order
            </th>
            <th scope="col" className="px-2 min-w-[250px] sm:px-6 py-1 sm:py-4">
              Name
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Measurement
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell min-w-[250px] px-2 sm:px-6 py-1 sm:py-4"
            >
              Type
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Rate
            </th>
            <th
              scope="col"
              className="hidden min-w-[150px] sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              CO2 Rate
            </th>
            <th
              scope="col"
              className="hidden min-w-[220px] sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Droppie Revenue (kg)
            </th>
            <th
              scope="col"
              className="hidden min-w-[170px]  sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              KG Conversion
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              CBS
            </th>
            <th
              scope="col"
              className="px-2 min-w-[150px] sm:px-6 py-1 sm:py-4 text-center"
            >
              Show in app
            </th>
            {canEditMaterials ? (
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-right">
                Actions
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {marketPrices?.map((item) => {
            const isActive = item?.isActive !== false;
            return (
              <tr
                key={item._id}
                className="bg-white hover:bg-gray-100 cursor-pointer transition ease-linear border-b"
              >
                <td
                  onClick={() => navigate(`/edit-market-pricing/${item?._id}`)}
                  className="px-2 sm:px-6 py-1 sm:py-4"
                >
                  {item?.order ?? 0}
                </td>
                <td
                  onClick={() => navigate(`/edit-market-pricing/${item?._id}`)}
                  className="px-2 sm:px-6 py-1 sm:py-4 break-all"
                >
                  {item?.name}
                </td>
                <td
                  onClick={() => navigate(`/edit-market-pricing/${item?._id}`)}
                  className="px-2 sm:px-6 py-1 sm:py-4"
                >
                  {item?.measurement}
                </td>
                <td
                  onClick={() => navigate(`/edit-market-pricing/${item?._id}`)}
                  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
                >
                  {item?.type}
                </td>
                <td
                  onClick={() => navigate(`/edit-market-pricing/${item?._id}`)}
                  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
                >
                  {Intl.NumberFormat("nl-NL", {
                    style: "currency",
                    currency: "EUR",
                  }).format(Number(item?.rate ?? 0))}
                  {item?.locationRates?.length ? (
                    <span className="block text-xs text-gray-text">
                      differs at {item.locationRates.length} location
                      {item.locationRates.length === 1 ? "" : "s"}
                    </span>
                  ) : null}
                </td>
                <td
                  onClick={() => navigate(`/edit-market-pricing/${item?._id}`)}
                  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
                >
                  {item?.co2_rate}
                </td>
                <td
                  onClick={() => navigate(`/edit-market-pricing/${item?._id}`)}
                  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
                >
                  {Intl.NumberFormat("nl-NL", {
                    style: "currency",
                    currency: "EUR",
                  }).format(Number(item?.droppie_revenue_kg ?? 0))}
                </td>
                <td
                  onClick={() => navigate(`/edit-market-pricing/${item?._id}`)}
                  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
                >
                  {item?.kgConversion}
                </td>
                <td
                  onClick={() => navigate(`/edit-market-pricing/${item?._id}`)}
                  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
                >
                  {item?.cbs}
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!item?._id) return;
                      updateMarketPricing(item._id as string, {
                        isActive: !isActive,
                      });
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      isActive ? "bg-droppie-oranje" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        isActive ? "translate-x-4" : "translate-x-1"
                      }`}
                    />
                  </button>
                </td>
                {canEditMaterials ? (
                  <td className="px-2 sm:px-6 py-1 sm:py-4 flex items-center justify-end cursor-pointer">
                    <Tooltip content="Edit">
                      <EditIcon
                        onClick={() =>
                          navigate(`/edit-market-pricing/${item?._id}`)
                        }
                        className="mr-3 sm:mr-7"
                      />
                    </Tooltip>
                    {canDeleteMaterials ? (
                      <Tooltip content="Delete">
                        <TrashIcon
                          onClick={() => {
                            const confirmDelete = window.confirm(
                              "Are you sure you want to delete this Stream?"
                            );
                            if (confirmDelete) {
                              deleteMarketPricing(item._id as string);
                            }
                          }}
                        />
                      </Tooltip>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      <DeleteModal isOpen={isOpen} closeModal={toggleModal} />
    </div>
  );
};
